// src/features/datev/components/DatevExports.tsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, RefreshCw, AlertCircle, ChevronDown, ChevronRight, Users,
  Calendar, FileText, Shield, Wallet, Receipt, BarChart3, Database
} from 'lucide-react';
import {
  datevExportsService,
  DatevExportClient,
  DatevExportMasterData
} from '../../../services/datev/DatevExportsService';
import { netlifyFetch } from '../../../shared/services/netlifyFetch';
import { verdecke } from '../werteSchutz';

interface DatevExportsProps {
  customerId: string;
  beraternummer: string;
  mandantennummer: string;
}

type CategoryKey = 'masterdata' | 'taxpayments' | 'socialsecurity' | 'salarytotals' | 'salarypayments' | 'absences';

interface CategoryDef {
  key: CategoryKey;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const CATEGORIES: CategoryDef[] = [
  { key: 'masterdata',     label: 'Stammdaten',         icon: <FileText size={14} />,  color: '#3B82F6' },
  { key: 'taxpayments',    label: 'Steuerzahlungen',    icon: <Receipt size={14} />,   color: '#8B5CF6' },
  { key: 'socialsecurity', label: 'SV-Beiträge',        icon: <Shield size={14} />,    color: '#10B981' },
  { key: 'salarytotals',   label: 'Lohnsummen',         icon: <BarChart3 size={14} />, color: '#F59E0B' },
  { key: 'salarypayments', label: 'Lohnzahlungen',      icon: <Wallet size={14} />,    color: '#EF4444' },
  { key: 'absences',       label: 'Abwesenheiten',      icon: <Calendar size={14} />,  color: '#6366F1' }
];

export const DatevExports: React.FC<DatevExportsProps> = ({
  customerId,
  beraternummer,
  mandantennummer
}) => {
  const clientId = `${beraternummer}-${mandantennummer}`;

  // State
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessHelpUrl, setAccessHelpUrl] = useState<string | null>(null);
  const [availableClients, setAvailableClients] = useState<{ consultant: string; ranges: string[]; count: number }[] | null>(null);
  const [clientInfo, setClientInfo] = useState<DatevExportClient | null>(null);

  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [employees, setEmployees] = useState<DatevExportMasterData[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [employeesHelpUrl, setEmployeesHelpUrl] = useState<string | null>(null);
  const [payrollSystem, setPayrollSystem] = useState<string | null>(null);

  const [expandedEmployees, setExpandedEmployees] = useState<Set<number>>(new Set());
  const [employeeData, setEmployeeData] = useState<Record<string, any>>({}); // key: `${employeeId}-${category}`
  const [loadingCategory, setLoadingCategory] = useState<string | null>(null);
  const [categoryError, setCategoryError] = useState<Record<string, string>>({});

  // Default-Monat: 2023-01 (Sandbox-Testdaten für Bestand 455148-1)
  useEffect(() => {
    setSelectedMonth('2023-01');
  }, []);

  // Berechtigungsprüfung — DATEV MUST: nicht als verbunden anzeigen, bevor /clients bestätigt
  const checkAccess = useCallback(async () => {
    setAccessError(null);
    setAccessHelpUrl(null);
    setAccessChecked(false);
    try {
      const info = await datevExportsService.getClient(customerId, clientId);
      setClientInfo(info);
      setHasAccess(true);
      setAccessChecked(true);
    } catch (err: any) {
      setHasAccess(false);
      setAccessChecked(true);
      // DATEV MUST: verständliche Fehlermeldung bei ungültiger Berater-/Mandantennummer
      if (err.requires_reconnect) {
        setAccessError('Keine DATEV-Verbindung. Bitte zunächst im Tab "Verbindung" mit DATEV verbinden.');
      } else if (err.error) {
        setAccessError(err.error + ' — Bitte überprüfen Sie Berater- und Mandantennummer oder kontaktieren Sie Ihren Administrator.');
      } else {
        setAccessError('Berechtigung konnte nicht geprüft werden. Bitte überprüfen Sie Berater- und Mandantennummer.');
      }
      setAccessHelpUrl(err.help_url || null);

      // Audit-Auflage 2: Bei Berechtigungsfehler freigegebene Mandanten anzeigen
      try {
        const res = await netlifyFetch(
          `https://intranet.xtax.de/.netlify/functions/datev-exports/clients?customerId=${customerId}`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.groups && data.groups.length > 0) {
            setAvailableClients(data.groups);
          }
        }
      } catch (e) {
        // Mandanten-Liste optional
      }
    }
  }, [customerId, clientId]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  // Mitarbeiter-Liste laden (Stammdaten = sichere Default-Quelle)
  const loadEmployees = async () => {
    if (!selectedMonth) return;

    setIsLoadingEmployees(true);
    setEmployeesError(null);
    setEmployeesHelpUrl(null);
    setEmployees([]);
    setExpandedEmployees(new Set());
    setEmployeeData({});
    setCategoryError({});
    setPayrollSystem(null);

    try {
      const data = await datevExportsService.getAllMasterData(
        customerId,
        clientId,
        { payroll_accounting_month: selectedMonth }
      );
      // Falls API ein einzelnes Objekt statt Array zurückgibt
      const list = Array.isArray(data) ? data : (data ? [data] : []);
      setEmployees(list);
    } catch (err: any) {
      setEmployeesError(err.error || 'Fehler beim Laden der Mitarbeiter');
      setEmployeesHelpUrl(err.help_url || null);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  // Kategorie-Daten für einzelnen Mitarbeiter abrufen
  const loadCategoryData = async (employeeId: number, category: CategoryKey) => {
    const key = `${employeeId}-${category}`;
    if (employeeData[key] !== undefined) return; // schon geladen

    setLoadingCategory(key);
    setCategoryError(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    try {
      const filter = { payroll_accounting_month: selectedMonth };
      let data: any;

      switch (category) {
        case 'masterdata':
          data = await datevExportsService.getEmployeeMasterData(customerId, clientId, employeeId, filter);
          break;
        case 'taxpayments':
          data = await datevExportsService.getEmployeeTaxPayments(customerId, clientId, employeeId, filter);
          break;
        case 'socialsecurity':
          data = await datevExportsService.getEmployeeSocialSecurityPayments(customerId, clientId, employeeId, filter);
          break;
        case 'salarytotals':
          data = await datevExportsService.getEmployeeSalaryTotalValues(customerId, clientId, employeeId, filter);
          break;
        case 'salarypayments':
          data = await datevExportsService.getEmployeeSalaryPayments(customerId, clientId, employeeId, filter);
          break;
        case 'absences':
          data = await datevExportsService.getEmployeeAbsences(customerId, clientId, employeeId, filter);
          break;
      }

      setEmployeeData(prev => ({ ...prev, [key]: data }));
    } catch (err: any) {
      setCategoryError(prev => ({
        ...prev,
        [key]: err.error || 'Fehler beim Abrufen der Daten'
      }));
    } finally {
      setLoadingCategory(null);
    }
  };

  const toggleExpand = (employeeId: number) => {
    setExpandedEmployees(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
        // Beim Aufklappen: Stammdaten direkt laden
        loadCategoryData(employeeId, 'masterdata');
      }
      return next;
    });
  };

  // Hilfsfunktion: Mitarbeitername aus Stammdaten extrahieren
  const getEmployeeName = (emp: DatevExportMasterData): string => {
    const personal = emp.personal_data || {};
    const first = personal.first_name || '';
    const last = personal.last_name || '';
    if (first || last) return `${last}${last && first ? ', ' : ''}${first}`.trim();
    return `Personalnummer ${emp.personnel_number}`;
  };

  // Monatsoptionen (letzte 24 Monate)
  const monthOptions: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 1; i <= 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(d);
    monthOptions.push({ value, label });
  }

  return (
    <div>
      {/* Überschrift mit Bestand-Identifikation (DATEV MUST: Daten der Firma zuordnen) */}
      <div style={{
        backgroundColor: '#EFF6FF',
        border: '1px solid #DBEAFE',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '20px',
        fontSize: '14px',
        color: '#1E40AF'
      }}>
        <strong>DATEV Lohnergebnisdatenservice (hr:exports):</strong> Strukturierte Lohndaten
        für Bestand <strong>{clientId}</strong> aus dem DATEV-Rechenzentrum.
        {payrollSystem && (
          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#1E3A8A' }}>
            · Lohnsystem: <strong>{payrollSystem.toUpperCase()}</strong>
          </span>
        )}
      </div>

      {/* Berechtigungsprüfung */}
      {!accessChecked && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
          <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: '8px' }} />
          Berechtigung wird geprüft...
        </div>
      )}

      {accessChecked && !hasAccess && (
        <div style={{
          backgroundColor: '#FEE2E2',
          border: '1px solid #FECACA',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '16px',
          fontSize: '14px',
          color: '#991B1B',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px'
        }}>
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong>Keine Berechtigung für diesen Bestand.</strong>
            <div style={{ marginTop: '4px' }}>{accessError}</div>
            {accessHelpUrl && (
              <div style={{ marginTop: '6px' }}>
                <a href={accessHelpUrl} target="_blank" rel="noopener noreferrer"
                   style={{ color: '#1E40AF', textDecoration: 'underline', fontSize: '13px' }}>
                  DATEV-Hilfe öffnen
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit-Auflage 2: Freigegebene Mandanten bei Berechtigungsfehler */}
      {availableClients && availableClients.length > 0 && !hasAccess && accessChecked && (
        <div style={{
          backgroundColor: '#FEF3C7',
          border: '1px solid #FDE68A',
          borderRadius: '6px',
          padding: '14px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#78350F'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>
            Für diesen Scope freigegebene Mandanten:
          </div>
          {availableClients.map((group, idx) => (
            <div key={idx} style={{ marginBottom: '6px' }}>
              <span style={{ fontWeight: '500' }}>Beraternummer {group.consultant}:</span>{' '}
              Mandanten {group.ranges.join(', ')}
              <span style={{ color: '#92400E', fontSize: '12px', marginLeft: '8px' }}>
                ({group.count} Mandanten)
              </span>
            </div>
          ))}
          <div style={{ marginTop: '10px', fontStyle: 'italic', color: '#92400E', fontSize: '12px' }}>
            Wenden Sie sich an Ihren Steuerberater, falls Mandanten fehlen.
          </div>
        </div>
      )}

      {/* Hauptinhalt nur bei Berechtigung */}
      {accessChecked && hasAccess && (
        <>
          {/* Bestätigung Berechtigungsprüfung */}
          <div style={{
            backgroundColor: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: '6px',
            padding: '10px 12px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#166534'
          }}>
            ✓ Berechtigung für Bestand {clientInfo?.client_id || clientId} bestätigt
            {clientInfo?.consultant_number && (
              <span> (Berater {clientInfo.consultant_number} · Mandant {clientInfo.client_number})</span>
            )}
          </div>

          {/* Monatsauswahl + Abrufen */}
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-end',
            marginBottom: '20px',
            flexWrap: 'wrap'
          }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                Abrechnungsmonat
              </label>
              <select
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  minWidth: '200px',
                  backgroundColor: 'white'
                }}
              >
                {monthOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                oder manuell (JJJJ-MM)
              </label>
              <input
                type="text"
                placeholder="z.B. 2020-01"
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  width: '150px',
                  backgroundColor: 'white'
                }}
              />
            </div>

            <button
              onClick={loadEmployees}
              disabled={isLoadingEmployees || !selectedMonth}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: !selectedMonth ? '#9CA3AF' : '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isLoadingEmployees || !selectedMonth ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: isLoadingEmployees ? 0.6 : 1
              }}
            >
              {isLoadingEmployees ? (
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Search size={16} />
              )}
              {isLoadingEmployees ? 'Lade Mitarbeiter...' : 'Mitarbeiter abrufen'}
            </button>
          </div>

          {/* Fehler beim Mitarbeiter-Laden */}
          {employeesError && (
            <div style={{
              backgroundColor: '#FEE2E2',
              border: '1px solid #FECACA',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#991B1B',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
              <div>
                {employeesError}
                {employeesHelpUrl && (
                  <div style={{ marginTop: '4px' }}>
                    <a href={employeesHelpUrl} target="_blank" rel="noopener noreferrer"
                       style={{ color: '#1E40AF', textDecoration: 'underline', fontSize: '13px' }}>
                      DATEV-Hilfe öffnen
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Mitarbeiter-Liste */}
          {employees.length > 0 && (
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
                fontSize: '14px',
                color: '#374151'
              }}>
                <Users size={16} />
                <strong>{employees.length} Mitarbeiter</strong>
                <span style={{ color: '#6B7280' }}>· Abrechnungsmonat: {selectedMonth}</span>
              </div>

              {employees.map(emp => {
                const isExpanded = expandedEmployees.has(emp.personnel_number);
                return (
                  <div key={emp.personnel_number} style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    overflow: 'hidden'
                  }}>
                    {/* Mitarbeiter-Header */}
                    <div
                      onClick={() => toggleExpand(emp.personnel_number)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 14px',
                        backgroundColor: '#F9FAFB',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      {isExpanded ? <ChevronDown size={16} color="#6B7280" /> : <ChevronRight size={16} color="#6B7280" />}
                      <Users size={16} color="#3B82F6" />
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>
                        {getEmployeeName(emp)}
                      </span>
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>
                        · PersNr {emp.personnel_number}
                        {emp.company_personnel_number && emp.company_personnel_number !== String(emp.personnel_number) &&
                          ` · Betr.PersNr ${emp.company_personnel_number}`}
                      </span>
                      <span style={{
                        marginLeft: 'auto',
                        fontSize: '12px',
                        color: '#6B7280',
                        backgroundColor: '#E5E7EB',
                        padding: '2px 8px',
                        borderRadius: '10px'
                      }}>
                        {emp.accounting_month}
                      </span>
                    </div>

                    {/* Drilldown: 6 Kategorien */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid #E5E7EB', backgroundColor: 'white' }}>
                        {CATEGORIES.map(cat => {
                          const dataKey = `${emp.personnel_number}-${cat.key}`;
                          const data = employeeData[dataKey];
                          const error = categoryError[dataKey];
                          const isLoading = loadingCategory === dataKey;
                          const hasData = data !== undefined;

                          return (
                            <div key={cat.key} style={{ borderBottom: '1px solid #F3F4F6' }}>
                              <div
                                onClick={() => loadCategoryData(emp.personnel_number, cat.key)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '8px 14px 8px 44px',
                                  cursor: hasData ? 'default' : 'pointer',
                                  fontSize: '13px',
                                  color: hasData ? '#111827' : '#3B82F6',
                                  backgroundColor: hasData ? '#F9FAFB' : 'transparent'
                                }}
                              >
                                <span style={{ color: cat.color }}>{cat.icon}</span>
                                <span style={{ fontWeight: '500' }}>{cat.label}</span>
                                {isLoading && (
                                  <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite', marginLeft: 'auto' }} />
                                )}
                                {!hasData && !isLoading && !error && (
                                  <span style={{ marginLeft: 'auto', fontSize: '12px' }}>Klicken zum Abrufen</span>
                                )}
                                {error && (
                                  <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#DC2626' }}>
                                    Fehler — erneut versuchen
                                  </span>
                                )}
                              </div>

                              {/* Fehler */}
                              {error && (
                                <div style={{
                                  padding: '8px 14px 12px 44px',
                                  fontSize: '12px',
                                  color: '#991B1B',
                                  backgroundColor: '#FEF2F2'
                                }}>
                                  {error}
                                </div>
                              )}

                              {/* Daten als formatiertes JSON anzeigen */}
                              {hasData && !error && (
                                <div style={{
                                  padding: '0 14px 12px 44px',
                                  fontSize: '12px'
                                }}>
                                  <pre style={{
                                    margin: 0,
                                    padding: '12px',
                                    backgroundColor: '#1F2937',
                                    color: '#F9FAFB',
                                    borderRadius: '4px',
                                    overflowX: 'auto',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    fontSize: '11px',
                                    lineHeight: '1.5',
                                    fontFamily: 'Menlo, Monaco, "Courier New", monospace'
                                  }}>
                                    {JSON.stringify(verdecke(data), null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Keine Mitarbeiter */}
          {!isLoadingEmployees && employees.length === 0 && !employeesError && (
            <div style={{
              padding: '24px',
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '14px',
              backgroundColor: '#F9FAFB',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <Database size={18} />
              Wählen Sie einen Abrechnungsmonat und klicken Sie auf "Mitarbeiter abrufen".
            </div>
          )}
        </>
      )}

      {/* CSS für Spin-Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
